<template>
  <div
    v-click-outside="hide"
    class="dropdown-wrapper"
    :class="{
      'dropdown-wrapper--opened': modelValue,
      'dropdown-wrapper--width-auto': widthAuto,
      'interview-style': interviewStyle
    }"
    data-testid="dropdown-wrapper"
    @mouseover.stop="onMouseOver"
    @mouseleave.stop="onMouseLeave"
  >
    <div
      ref="toggle"
      class="dropdown-toggle"
      data-testid="dropdown-toggle"
      :class="[
        backgroundTypeClass,
        {
          'dropdown-toggle--borders': toggleWithBorders,
          'dropdown-toggle--icon-prefix': labelPrefixIcon,
          'dropdown-toggle--hover': showOnHover
        }
      ]"
      @click.prevent="toggle"
    >
      <slot />
      <span
        v-if="isSuffixIcon"
        class="suffix-icon"
        :class="iconTypeClass"
      >
        <IconDropdown />
      </span>
    </div>

    <div
      v-if="modelValue"
      ref="dropdown"
      class="dropdown__content"
      :class="{
        'disable-mobile': disableMobileVersion,
        [dropdownBackgroundType]: true
      }"
      :style="contentStyle"
      data-testid="dropdown-content"
    >
      <ScrollBar faded>
        <slot name="dropdown" />
      </ScrollBar>
    </div>
  </div>
</template>

<script>
import ScrollBar from '@/components/scroll/ScrollBar.vue'
import vClickOutside from 'click-outside-vue3'

import IconDropdown from '@/components/icons/IconDropdown.vue'

export default {
  name: 'BaseDropdown',
  directives: {
    clickOutside: vClickOutside.directive
  },
  components: {
    ScrollBar,
    IconDropdown
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    disableMobileVersion: {
      type: Boolean,
      default: false
    },
    iconType: {
      type: String,
      default: 'default',
      validator(value) {
        return ['primary', 'default', 'white'].includes(value)
      }
    },
    showOnHover: {
      type: Boolean,
      default: false
    },
    toggleWithBorders: {
      type: Boolean,
      default: false
    },
    isSuffixIcon: {
      type: Boolean,
      default: true
    },
    toggleWithBackground: {
      type: Boolean,
      default: false
    },
    backgroundType: {
      type: String,
      default: 'unset',
      validator(value) {
        return ['unset', 'highlight', 'disabled'].includes(value)
      }
    },
    dropdownBackgroundType: {
      type: String,
      default: 'default',
      validator(value) {
        return ['default', 'highlight'].includes(value)
      }
    },
    labelPrefixIcon: {
      type: Boolean,
      default: false
    },
    widthAuto: {
      type: Boolean,
      default: false
    },
    dropdownContentWidth: {
      type: Number,
      default: 270
    },
    interviewStyle: {
      type: Boolean,
      default: false
    },
    useNegativePositioning: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      dropdownTop: 0,
      dropdownLeft: 0
    }
  },
  computed: {
    iconTypeClass() {
      const { iconType } = this
      return `suffix-icon--${iconType}`
    },
    backgroundTypeClass() {
      const { backgroundType } = this
      return `dropdown-toggle--${backgroundType}`
    },
    contentStyle() {
      const { dropdownContentWidth, dropdownTop, dropdownLeft } = this
      return {
        width: `${dropdownContentWidth}px`,
        top: `${dropdownTop}px`,
        left: `${dropdownLeft}px`
      }
    }
  },
  watch: {
    modelValue: {
      async handler(modelValue) {
        if (!modelValue) return
        await this.calculatePosition()
      },
      immediate: true
    }
  },
  mounted() {
    window.addEventListener('scroll', this.calculatePosition)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.calculatePosition)
  },
  methods: {
    show() {
      this.$emit('update:modelValue', true)
    },
    hide() {
      this.$emit('update:modelValue', false)
    },
    toggle() {
      this.$emit('update:modelValue', !this.modelValue)
    },
    onMouseOver() {
      if (this.showOnHover) {
        this.show()
      }
    },
    onMouseLeave() {
      if (this.showOnHover) {
        this.hide()
      }
    },
    async calculatePosition() {
      if (!this.modelValue) return

      await this.$nextTick()
      const { toggle } = this.$refs
      const { right, bottom, height, width, x, y } =
        toggle.getBoundingClientRect()
      const horizontalSpaceBelow = window.innerWidth - right
      const verticalSpaceBelow = window.innerHeight - bottom
      const dropdownRect = this.$refs.dropdown?.getBoundingClientRect()
      const dropdownHeight = dropdownRect.height
      const dropdownWith = dropdownRect.width

      this.dropdownTop =
        y + (dropdownHeight > verticalSpaceBelow ? -dropdownHeight : height)
      this.dropdownLeft =
        x +
        (dropdownWith > horizontalSpaceBelow
          ? (this.useNegativePositioning ? width - dropdownWith : Math.max(0, width - dropdownWith))
          : 0)
    }
  }
}
</script>

<style scoped lang="scss">
.dropdown-wrapper {
  position: relative;
  outline: none;
  display: block;

  .dropdown__content {
    position: fixed;
    overflow: hidden;
    display: flex;
    max-height: 370px;
    box-shadow: var(--input-select-shadow);
    border-radius: var(--border-radius-4);
    z-index: 40;

    &.default {
      background: var(--color-white);
    }

    &.highlight {
      background: var(--color-yellow-50);
      color: var(--color-zinc-700);
    }
  }

  &.dropdown-wrapper--width-auto {
    .dropdown__content {
      width: auto;
    }
  }

  ::v-deep(.ps__rail-y) {
    display: none;
  }

  @include phone {
    &.dropdown-wrapper--opened {
      &::after {
        content: '';
        background-color: var(--color-opacity-black-30);
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        position: fixed;
        pointer-events: none;
        z-index: 40;
      }

      .dropdown__content {
        &:not(&.disable-mobile) {
          top: unset !important;
          left: 0 !important;
          width: 100vw !important;
          bottom: 0 !important;
          position: fixed;
          right: 0;
          max-height: 70vh;
          border-radius: var(--space-12) var(--space-12) 0 0;
          z-index: 1001;
        }
      }
    }
  }

  .dropdown-toggle {
    position: relative;
    z-index: 0;
    cursor: pointer;
    font-size: var(--font-size-body-2);
    line-height: var(--leading-170);
    font-weight: var(--weight-normal);
    display: flex;
    align-items: center;
    column-gap: var(--space-12);

    &.dropdown-toggle--borders {
      justify-content: space-between;
      column-gap: var(--space-20);
      border: 1px solid var(--color-zinc-200);
      border-radius: var(--border-radius-8);
      padding: var(--space-4) var(--space-12);
      width: 100%;
      background-color: var(--color-white);
    }

    &.dropdown-toggle--highlight {
      background-color: var(--color-yellow-50);
      border-radius: var(--border-radius-8);
      justify-content: space-between;
      column-gap: var(--space-20);
      padding: var(--space-4) var(--space-12);
      width: 100%;
    }

    &.dropdown-toggle--default {
      background-color: var(--color-zinc-50);
      border-radius: var(--border-radius-8);
      justify-content: space-between;
      column-gap: var(--space-20);
      padding: var(--space-4) var(--space-12);
      width: 100%;
    }

    &.dropdown-toggle--icon-prefix {
      flex-direction: row-reverse;
    }

    .suffix-icon {
      display: flex;

      &.suffix-icon--default {
        color: var(--color-zinc-400);
      }

      &.suffix-icon--primary {
        color: var(--color-brand-500);
      }

      &.suffix-icon--white {
        color: var(--color-white);
      }
    }

    &.dropdown-toggle--hover {
      .suffix-icon {
        display: none;
      }

      & ~ .dropdown__content-wrap {
        box-shadow: var(--input-select-shadow);

        .suffix-icon {
          display: none;
        }
      }
    }
  }

  &.interview-style {
    .dropdown__content {
      box-shadow: var(--shadow-drop);
      border-radius: var(--border-radius-8);
      background: var(--color-zinc-50);
      padding: var(--space-4);
      margin: var(--space-6) 0;
    }

    .dropdown-toggle {
      .suffix-icon {
        &.suffix-icon--primary {
          color: var(--color-zinc-950);
        }
      }
    }

    &.dropdown-wrapper--opened {
      outline: none;
      box-shadow: 0 0 0 6px var(--color-zinc-200);
      border-radius: var(--border-radius-8);
      z-index: 1;

      .dropdown-toggle {
        border-color: var(--color-brand-500) !important;
      }
    }
  }
}
</style>
